import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const GeorgiaCloudConsultingPage = () => {
  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Cloud Consulting in Georgia',
    url: 'https://thealphaconsult.com/georgia-cloud-consulting',
    serviceType: 'Cloud consulting',
    provider: {
      '@type': 'ProfessionalService',
      name: 'Alpha Consulting LLC',
      image: 'https://thealphaconsult.com/logo.png',
      telephone: '+1-770-545-1557',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Atlanta',
        addressRegion: 'GA',
        addressCountry: 'US'
      }
    },
    areaServed: {
      '@type': 'State',
      name: 'Georgia'
    },
    description: 'Cloud consulting services for Georgia organizations modernizing infrastructure, applications, and operations.'
  };

  return (
    <div className="pt-24">
      <SEO
        title="Cloud Consulting in Georgia | Alpha Consulting LLC"
        description="Georgia cloud consulting for application modernization, infrastructure automation, DevOps, integrations, observability, and scalable platform delivery."
        keywords="cloud consulting Georgia, cloud migration Georgia, DevOps consulting Georgia, infrastructure automation Atlanta, cloud modernization Georgia, platform engineering Georgia"
        url="https://thealphaconsult.com/georgia-cloud-consulting"
        image="https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
        structuredData={pageSchema}
      />

      <section className="relative py-20 bg-gradient-to-b from-orange-950 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-wider text-amber-300/90 font-nunito">
              Georgia cloud consulting
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl tracking-tight font-nunito font-bold">
              Cloud consulting for Georgia teams that need scalable systems
            </h1>
            <p className="mt-6 text-lg text-orange-300 font-nunito">
              We help Georgia organizations modernize infrastructure, improve deployment pipelines, strengthen observability, and build cloud-native systems that support reliable growth.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-lg font-semibold font-nunito">Cloud architecture</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                Design resilient platforms with the right balance of speed, reliability, and cost control.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-lg font-semibold font-nunito">Infrastructure automation</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                Reduce manual setup with infrastructure as code, environment standardization, and repeatable delivery.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-lg font-semibold font-nunito">DevOps enablement</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                Improve release quality and speed with CI/CD, testing, monitoring, and operational guardrails.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-lg font-semibold font-nunito">Modernization</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                Refactor legacy systems, improve integrations, and move toward scalable cloud platforms without unnecessary disruption.
              </p>
            </div>
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-black/40 p-8">
            <h2 className="text-2xl font-semibold font-nunito">Where cloud consulting helps most</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <p className="text-orange-300 font-nunito">
                If your team is struggling with brittle deployments, limited visibility, growing infrastructure spend, or environments that only one person understands, cloud consulting can create stability quickly.
              </p>
              <p className="text-orange-300 font-nunito">
                We help teams define a practical roadmap for modernization, whether that means improving an existing platform, migrating selected workloads, or building the foundation for new products.
              </p>
            </div>
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-orange-900/40 p-8">
            <h2 className="text-2xl font-semibold font-nunito">Typical outcomes</h2>
            <ul className="mt-6 grid gap-4 md:grid-cols-2 text-orange-300 font-nunito">
              <li>Faster and safer releases with CI/CD and test automation</li>
              <li>More predictable cloud environments through infrastructure as code</li>
              <li>Improved uptime with monitoring, alerting, and runbooks</li>
              <li>Better visibility into cost, performance, and operational risk</li>
            </ul>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-100 text-black hover:bg-orange-200 transition-all hover:scale-[1.02] font-nunito font-medium"
            >
              Discuss your cloud roadmap
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GeorgiaCloudConsultingPage;
